import type { BudgetModelConfig, CategoryId, ContractDocument } from '@timelog/pm-shared';
import {
  CATEGORY_IDS,
  getCategory,
  isCategoryAvailable,
  upliftIsVisible,
} from '@timelog/pm-shared';

import type { ContractEditKey } from '../editor/contractStore';

/**
 * The Financials keyboard path (design v8): Enter/Tab commits and advances
 * through fields → cards, in visual order. This module is the pure order —
 * the reducer's `START_EDITING` does the "focusing" (InlineTextCell mounts
 * its input and focuses when its key becomes active), so advancing is one
 * dispatch, no DOM traversal.
 *
 * Split/supplier ROWS advance locally inside their sections (they own row ids
 * and expansion state); this order covers the headline fields of the cards.
 */

export type FocusOrderOptions = {
  config: BudgetModelConfig;
};

const CONFIGURABLE: readonly CategoryId[] = ['cont', 'exp', 'sub', 'mat'];

/** Headline edit keys in visual order for the active contract. */
export const contractFocusOrder = (
  document: ContractDocument,
  { config }: FocusOrderOptions,
): ContractEditKey[] => {
  const keys: ContractEditKey[] = ['revenue'];

  const participates = (id: CategoryId): boolean => {
    const category = getCategory(document, id);
    return (
      isCategoryAvailable(config, id) &&
      !category.hidden &&
      category.billedVia === undefined &&
      (id === 'own' || CONFIGURABLE.includes(id))
    );
  };

  for (const id of CATEGORY_IDS) {
    if (!participates(id)) continue;
    if (id === 'own') {
      keys.push('own-hours');
    } else {
      keys.push(`amount:${id}`);
    }
    // Per section since schema 2: a card whose uplift the config fixes renders a
    // static pill, so Tab must not stop at a field that has no input.
    if (upliftIsVisible(config, id)) keys.push(`factor:${id}`);
  }
  return keys;
};

/** The key after/before `current` in the order; null falls off either end. */
export const nextEditKey = (
  order: readonly ContractEditKey[],
  current: ContractEditKey,
  direction: 1 | -1 = 1,
): ContractEditKey | null => {
  const index = order.indexOf(current);
  if (index === -1) return null;
  return order[index + direction] ?? null;
};
